import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import { ArrowDropDown } from '@mui/icons-material'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { setSelectedTheme } from 'redux/slices/Themes'
import { fetchContributions } from 'redux/thunks/TextAnalysis'
import { getThemes } from 'redux/thunks/Themes'
import { ClickEvent } from '../Helpers/Helper'
import { getTooltipTitle } from '../Helpers/ThemesHelper'
import { Theme } from '../AIThemes/ThemeKeyPhrasesModal/Types'

const ThemesFilter = () => {
  const dispatch = useAppDispatch()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const {
    data: allData,
    contributions,
    appConfig: { themes_keyphrasesToggle, platformThemes_keyphrases },
  } = useAppSelector((state) => state.textAnalysis.main)
  const { themes, selectedTheme, status } = useAppSelector(
    (state) => state.textAnalysis.theme,
  )

  const allThemes = themes?.data || []
  const fetching = contributions?.allContributions?.fetching || false
  const getLoading = status?.get?.loading || false

  const handleThemeClick = useCallback((event: ClickEvent) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleThemeSelect = useCallback(
    (theme: Theme | null) => {
      const attributes = theme?.attributes
      const keyPhrases = attributes?.query?.attributes?.key_phrases ?? []
      const obj = {
        name: attributes?.name ?? '',
        key_phrases: keyPhrases.length ? keyPhrases : [''],
      }
      dispatch(setSelectedTheme(theme ? obj : null))
      setAnchorEl(null)
      dispatch(fetchContributions())
    },
    [dispatch],
  )

  const noThemes = useMemo(() => allThemes.length === 0, [allThemes])
  const hasData = useMemo(() => allData?.length > 0, [allData])
  const isDisabled = useMemo(() => fetching || noThemes, [fetching, noThemes])

  const tooltipTitle = getTooltipTitle(getLoading, noThemes)

  useEffect(() => {
    if (noThemes && hasData && themes_keyphrasesToggle) {
      dispatch(getThemes())
    }
  }, [noThemes, hasData, dispatch, themes_keyphrasesToggle])

  // If platform or tool level feature is disabled, don't render anything
  if (!platformThemes_keyphrases || !themes_keyphrasesToggle) return null

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <Button
            variant={selectedTheme ? 'contained' : 'outlined'}
            disableElevation
            size="small"
            sx={{
              height: '40px',
              width: { xs: '100%', md: 'auto' },
              ...(selectedTheme ? {} : { color: 'primary.dark' }),
            }}
            onClick={handleThemeClick}
            startIcon={<ArrowDropDown />}
            disabled={isDisabled}
            data-testid="themes-filter-button"
          >
            {selectedTheme?.name || 'All Themes'}
          </Button>
        </span>
      </Tooltip>
      <Menu
        data-testid="themes-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableAutoFocusItem
        disablePortal
      >
        <MenuItem
          selected={!selectedTheme}
          onClick={() => handleThemeSelect(null)}
        >
          <ListItemText>All Themes</ListItemText>
        </MenuItem>
        {allThemes.map((theme: Theme) => (
          <MenuItem
            key={theme.id ?? theme.attributes.name}
            selected={selectedTheme?.name === theme.attributes.name}
            onClick={() => handleThemeSelect(theme)}
          >
            <ListItemText>{theme.attributes.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export { ThemesFilter }
